import { useMemo, useState } from 'react';

import { Box, useMediaQuery, useTheme } from '@mui/material';
import { subDays, subHours } from 'date-fns';

import { type ListingInsightsQueryVariables } from '@graphql/__generated__/graphql';
import { SegmentedToggleButtonGroup } from '@modules/_shared/components/SegmentedToggleButtonGroup';
import {
  CONTENT_HEIGHT,
  TwoPaneLayout,
} from '@modules/_shared/components/TwoPaneLayout';
import { ListingFeed } from '@modules/listings/ListingFeed';
import { useLocale } from '@shared/hooks/locale';

import { ActivityCardBoundary } from './ActivityCardBoundary';
import { CoachCallouts, useCoachCallouts } from './ListingCoachCallouts';
import { ListingComparablesCard } from './ListingComparablesCard';
import { ListingFunnel } from './ListingFunnel';
import { ListingPulseCard } from './ListingPulseCard';
import { ListingTransactionsCard } from './ListingTransactionsCard';

export const ListingActivity = ({
  lotId,
  readOnly = false,
}: {
  lotId: string;
  readOnly?: boolean;
}) => {
  const { t } = useLocale();
  const theme = useTheme();
  const isStacked = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileView, setMobileView] = useState<'activity' | 'stats'>(
    'activity',
  );
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const dismiss = (id: string) => setDismissed(prev => new Set(prev).add(id));

  // Fixed momentum/coach windows. Memoize on the lot so the same variables feed
  // (and dedupe) the pulse + coach queries.
  const insightsVars = useMemo<ListingInsightsQueryVariables>(() => {
    const now = new Date();
    return {
      lotId,
      staleFloor: subDays(now, 60).toISOString(),
      staleBefore: subHours(now, 24).toISOString(),
      recentFrom: subDays(now, 7).toISOString(),
      priorFrom: subDays(now, 35).toISOString(),
      monthFrom: subDays(now, 28).toISOString(),
      now: now.toISOString(),
    };
  }, [lotId]);

  // Coach callouts are computed once here so the banner (orphan tips) and the
  // funnel (section tips) share one source of truth and one dismissal set.
  const callouts = useCoachCallouts(lotId, insightsVars);
  const visibleCallouts = callouts.filter(c => !dismissed.has(c.id));
  const bannerCallouts = visibleCallouts.filter(c => c.section == null);

  const stats = (
    <Box>
      {/* Orphan coach tips (no funnel section) sit above the card stack so the
          accordions below stay visually connected as one group. Section tips
          live inside the funnel. */}
      {bannerCallouts.length > 0 && (
        <Box sx={{ px: { xs: 2, md: 0 }, mb: 1.5 }}>
          <CoachCallouts callouts={bannerCallouts} onDismiss={dismiss} />
        </Box>
      )}
      {/* Pulse, the funnel (which holds the per-step trend charts + their
          section tips) and comparables form one connected accordion stack.
          Each card is isolated so one failing card can't white-screen the
          tab. */}
      <ActivityCardBoundary key={`pulse-${lotId}`} name={t('Pulse')} t={t}>
        <ListingPulseCard lotId={lotId} insightsVars={insightsVars} />
      </ActivityCardBoundary>
      <ActivityCardBoundary
        key={`funnel-${lotId}`}
        name={t('Conversion funnel')}
        t={t}
      >
        <ListingFunnel
          lotId={lotId}
          callouts={visibleCallouts}
          onDismissCallout={dismiss}
        />
      </ActivityCardBoundary>
      <ActivityCardBoundary
        key={`comparables-${lotId}`}
        name={t('Comparable listings')}
        t={t}
      >
        <ListingComparablesCard lotId={lotId} />
      </ActivityCardBoundary>
      <ActivityCardBoundary
        key={`transactions-${lotId}`}
        name={t('Comparable transactions')}
        t={t}
      >
        <ListingTransactionsCard lotId={lotId} />
      </ActivityCardBoundary>
    </Box>
  );

  const feed = <ListingFeed readOnly={readOnly} />;

  // The feed is virtualized (react-virtuoso) and needs a dedicated scroll
  // parent, so it can't flow inline under the stats on mobile. Instead we keep
  // both mounted and toggle visibility, each taking the full height.
  if (isStacked) {
    return (
      <Box
        sx={{
          height: CONTENT_HEIGHT,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{ p: 1, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
        >
          <SegmentedToggleButtonGroup
            fullWidth
            value={mobileView}
            onChange={setMobileView}
            options={[
              { value: 'activity', label: t('Activity') },
              { value: 'stats', label: t('Statistics') },
            ]}
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: mobileView === 'activity' ? 'flex' : 'none',
          }}
        >
          {feed}
        </Box>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            py: 2,
            // No horizontal padding: cards are full-bleed on mobile (the
            // accordion zeroes its side borders/radius under `md`). The
            // controls row keeps its own inset via `px` below.
            px: 0,
            display: mobileView === 'stats' ? 'block' : 'none',
          }}
        >
          {stats}
        </Box>
      </Box>
    );
  }

  return (
    <TwoPaneLayout
      leftPane={stats}
      rightPane={feed}
      leftPaneSx={{ flex: '1 1 0' }}
      rightPaneSx={{ flex: '1 1 0', p: 0, gap: 0, overflow: 'hidden' }}
    />
  );
};
